import React, { useEffect, useState } from 'react'
import ComponentHeader from '../CommonComponents/componentHeader';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from '../CommonComponents/loadingSpinner';
import { setWeeklyNotificationEmails } from '../../Actions/notificationEmailsActions';
import { deepEqualArrays, isValidEmail } from '../../Util/jsUtils';
import { weeklyNotificationEmailsSelector } from '../../Reducers/weeklyNotificationEmailsReducer';
import { cx } from '../../Util/classHelpers';
import { getReviewNotificationEmails, setReviewNotificationEmails } from '../../Actions/notificationEmailsActions';
import { reviewNotificationEmailsSelector } from '../../Reducers/reviewNotificationEmailsReducer';

export const NOTIFICATIONS_COMPONENT = "NOTIFICATIONS_COMPONENT";

const EmailList = ({ emails, isLoading }) => {
  if (isLoading) {
    return (
      <div className="pt-2 text-muted small">
        <em>Updating...</em>
      </div>
    );
  }
  if (emails.length === 0) {
    return (
      <div className="pt-2 text-muted small">
        <em>No Emails Added</em>
      </div>
    );
  }
  return (
    <div className="d-flex flex-wrap gap-2">
      {emails.map((email, index) => (
        <span key={index} className="email-badge leading-4_5">{email}</span>
      ))}
    </div>
  );
};

const EmailInputs = ({ initialEmails, submitHandler, maxEmails = 10 }) => {
  const initialEmailObjects = initialEmails.map((email, index) => ({
    key: index,
    value: email,
  }));
  const [emailInputs, setEmailInputs] = useState(initialEmailObjects);
  const [newEmail, setNewEmail] = useState("");

  const handleDelete = (key) => {
    const updatedEmails = emailInputs.filter((email) => key !== email.key);
    setEmailInputs(updatedEmails);
  };

  const handleAddInput = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (isValidEmail(newEmail)) {
        const newKey = emailInputs.reduce((prev, cur) => Math.max(prev, cur.key), 0) + 1;
        setEmailInputs([...emailInputs, { value: newEmail, key: newKey }]);
        setNewEmail("");
      }
    }
  };

  const canSave = !deepEqualArrays(emailInputs, initialEmailObjects);

  const handleSave = (e) => {
    e.preventDefault();
    submitHandler(emailInputs.map((email) => email.value));
  };

  const sortedEmails = emailInputs.sort((a, b) => a.key - b.key);

  return (
    <form onSubmit={handleSave}>
      <div className="email-inputs d-flex flex-wrap gap-2">
        {sortedEmails.map((email) => (
          <div key={email.key} className="email-badge leading-4_5">
            {email.value}{" "}
            <span
              className="btn p-0 m-0 leading-4_5"
              onClick={() => handleDelete(email.key)}
            >
              &#x2715;
            </span>
          </div>
        ))}
        {emailInputs.length < maxEmails && (
          <input
            type="text"
            className="block"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={handleAddInput}
            placeholder="Enter Email"
          />
        )}
      </div>
      {canSave && (
        <div className="mt-2">
          <button className="btn btn-success" type="submit" disabled={newEmail.length > 0}>
            <i className="bi bi-check-circle"></i> Save
          </button>
        </div>
      )}
    </form>
  );
};

const EmailsView = ({
  label,
  subtitle,
  emails,
  isLoading,
  onSubmit,
  maxEmails = 10,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setIsEditing(false);
  }, [emails]);

  const submitHandler = (newEmails) => {
    onSubmit(newEmails);
    setIsEditing(false);
  };

  return (
    <div className={cx("mt-4 d-flex flex-column")}
      style={{ paddingLeft: "8px", paddingRight: "8px" }}>
      <span className="sub-title">{label}</span>
      <div className="mb-2 d-flex justify-content-between align-items-center">
        <div>{subtitle}</div>
        {isEditing ? (
          <button className={cx("btn")}
            onClick={() => setIsEditing(false)}>
            <span className="text-danger">
              <i className="bi bi-x-circle"></i> Cancel
            </span>
          </button>
        ) : (
          <button className={"btn"}
            onClick={() => setIsEditing(true)}>
            <span className="text-success">
              <i className="bi bi-pencil"></i> Edit
            </span>
          </button>
        )}
      </div>
      {isEditing ? (
        <EmailInputs initialEmails={emails} submitHandler={submitHandler} maxEmails={maxEmails} />
      ) : (
        <EmailList emails={emails} isLoading={isLoading} />
      )}
    </div>
  );
};

const ReportEmailsSection = ({ isLoading, setIsLoading }) => {
  const emailsObj = useSelector(weeklyNotificationEmailsSelector);
  const dispatch = useDispatch();
  const emails = emailsObj?.getEmails() ?? [];
  const [showLoading, setShowLoading] = useState(false);

  const submitEmails = (newEmails) => {
    setIsLoading(true);
    setShowLoading(true);
    dispatch(setWeeklyNotificationEmails(newEmails));
  };

  useEffect(() => {
    if (emailsObj) {
      setIsLoading(false);
      setShowLoading(false);
    }
  }, [emailsObj, setIsLoading, setShowLoading]);

  return (
    <EmailsView
      label="Weekly Reports Emails"
      subtitle="Manage the email addresses where you want to receive Weekly Reports for your companies"
      emails={emails}
      isLoading={showLoading}
      onSubmit={submitEmails}
    />
  );
};

const ReviewEmailsSection = ({ isLoading, setIsLoading }) => {
  const emailsObj = useSelector(reviewNotificationEmailsSelector);
  const dispatch = useDispatch();
  const emails = emailsObj?.getEmails() ?? [];
  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    dispatch(getReviewNotificationEmails());
  }, [dispatch]);

  useEffect(() => {
    if (emailsObj) {
      setIsLoading(false);
      setShowLoading(false);
    }
  }, [emailsObj, setIsLoading, setShowLoading]);

  const submitEmails = (newEmails) => {
    setIsLoading(true);
    setShowLoading(true);
    dispatch(setReviewNotificationEmails(newEmails));
  };

  return (
    <EmailsView
      label="Review Emails"
      subtitle="Manage the email addresses to receive alerts for low-rated reviews"
      emails={emails}
      isLoading={showLoading}
      onSubmit={submitEmails}
    />
  );
};

const NotificationsWrapper = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  return (
    <>
      <ComponentHeader title={"Notifications"} />
      <div className={cx("d-flex flex-column gap-4", isLoading && "disabled-div")}>
        <ReportEmailsSection isLoading={isLoading} setIsLoading={setIsLoading} />
        <ReviewEmailsSection isLoading={isLoading} setIsLoading={setIsLoading} />

        {isLoading && <div>
            <LoadingSpinner/>
        </div>}
      </div>
    </>
  );
};

export default NotificationsWrapper;
